// The /threads scope picker — lifted out of Composer.jsx when the page grew a
// top bar (SIDEBAR_LENS_REWORK): the scope still RIDES each ask (the server
// stores it as the thread's `last_scope`), but the control now lives in the
// bar, where the active lens's name doubles as the page's scope readout.
//
// LensProvider is mounted only on lens routes and /threads is outside it, so
// this owns its own fetch — lazily, on the first open, because most sends
// never touch the picker. A fetch failure leaves the menu saying so and
// Global still selectable — the picker must never become the reason a
// question can't be asked.
//
// `direction`: the composer rendered this above itself ("up"); the top bar
// renders it below itself ("down").
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";

import { listLenses } from "../../../api/threadsChat";
import { feedViewIcon } from "../Feeds/feedConstants";

const LensScopePicker = ({
  scope,
  scopeMissing,
  onChange,
  disabled,
  direction = "up",
}) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [lenses, setLenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next && !fetchedRef.current) {
        fetchedRef.current = true;
        setLoading(true);
        listLenses()
          .then((rows) => setLenses(Array.isArray(rows) ? rows : []))
          .catch(() => setError("Couldn't load lenses"))
          .finally(() => setLoading(false));
      }
      return next;
    });
  }, []);

  const isGlobal = scope === "global" || !scope?.id;
  const activeId = isGlobal ? null : scope.id;
  const ActiveIcon = isGlobal ? Globe : feedViewIcon(scope.icon);
  const label = isGlobal
    ? scopeMissing
      ? "Global (lens deleted)"
      : "Global"
    : scope.name || "Lens";

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        title={
          isGlobal
            ? scopeMissing
              ? "This thread's saved lens no longer exists — answers are scoped to the whole corpus"
              : "Scope: the whole corpus. Pick a saved lens to narrow it."
            : `Scope: ${scope.name}`
        }
        className="inline-flex h-8 max-w-[220px] items-center gap-1.5 rounded-lg border border-mainDivider bg-mainButtonBg/35 px-2.5 text-xs text-mainText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-velBlue disabled:cursor-not-allowed disabled:opacity-50"
      >
        <ActiveIcon
          size={12}
          className="shrink-0"
          style={isGlobal ? undefined : { color: scope.color || "#38bdf8" }}
        />
        <span className="truncate">{label}</span>
        <ChevronDown
          size={11}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className={`absolute left-0 z-40 w-64 overflow-hidden rounded-xl border border-mainDivider bg-mainBg/95 p-1.5 shadow-[0_0_0_1px_rgba(14,165,233,0.06),0_22px_48px_rgba(0,0,0,0.35)] backdrop-blur-md ${
            direction === "down"
              ? "top-[calc(100%+6px)]"
              : "bottom-[calc(100%+6px)]"
          }`}
        >
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange("global");
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition ${
                isGlobal
                  ? "bg-velBlue/10 text-mainText"
                  : "text-mutedText hover:bg-mainButtonBg/45 hover:text-mainText"
              }`}
            >
              <Globe size={13} className="shrink-0 text-velBlue/80" />
              <span className="truncate">Global</span>
              {isGlobal && (
                <Check size={12} className="ml-auto shrink-0 text-velBlue" />
              )}
            </button>

            {loading && (
              <div className="px-2 py-3 text-center text-[11px] text-mutedText">
                Loading lenses…
              </div>
            )}
            {!loading && error && (
              <div className="px-2 py-3 text-center text-[11px] text-mutedText">
                {error}
              </div>
            )}
            {!loading && !error && lenses.length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-mutedText">
                No saved lenses yet.
              </div>
            )}
            {lenses.map((lens) => {
              const active = lens.id === activeId;
              const LensIcon = feedViewIcon(lens.icon);
              return (
                <button
                  key={lens.id}
                  type="button"
                  onClick={() => {
                    // The wire shape is {kind, id, name}; `kind` defaults to
                    // "view" because the pool endpoint does not label origin
                    // and the server resolves a preset by id either way.
                    onChange({
                      kind: lens.kind || "view",
                      id: lens.id,
                      name: lens.name,
                      icon: lens.icon,
                      color: lens.color,
                    });
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition ${
                    active
                      ? "bg-velBlue/10 text-mainText"
                      : "text-mutedText hover:bg-mainButtonBg/45 hover:text-mainText"
                  }`}
                >
                  <LensIcon
                    size={13}
                    className="shrink-0"
                    style={{ color: lens.color || "#38bdf8" }}
                  />
                  <span className="truncate">{lens.name}</span>
                  {active && (
                    <Check size={12} className="ml-auto shrink-0 text-velBlue" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LensScopePicker;
